import { useEffect, useState, useRef } from 'react';
import { StyledConnectAccountSteps } from './ConnectAccountStepsStyles';
import { FaLinkedin } from 'react-icons/fa';
import Button from '../../../molecules/Button';
import linkedinService from '../../../../services/linkedinService';
import Toast from '../../../molecules/Toast';
import Loaders from '../../Loaders';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../../../Context/authContext';
import stickLoading from '../../../../assets/images/stick-animation.gif';
import { CiLock } from 'react-icons/ci';


const Connection = ({ onClose, setStep, accountData, setAccountData, activeProfile, isLinkedinLogin, onIframeStateChange, proxyLoading }) => {
  const [dynamicUrl, setDynamicUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [verifyError, setVerifyError] = useState(false);
  const hasInitiatedLogin = useRef(false);
  const { refetch } = useContextHook(AuthContext, v => ({
    refetch: v.refetch,
  }));

  const getLoginPayload = () => {
    if (isLinkedinLogin) {
      return {
        country: activeProfile?.country,
        profileId: activeProfile?.id,
      };
    }

    return {
      cookies: [],
      country: accountData?.proxy?.country || activeProfile?.country,
      profileId: accountData?.profileId || activeProfile?.id,
    };
  };

  async function handleLinkedinLogin(isRetry = false) {
    if (!isRetry && (hasInitiatedLogin.current || loginLoading || dynamicUrl)) {
      return;
    }
    hasInitiatedLogin.current = true;
    setLoginLoading(true);

    try {
      setLoginError(false);
      setVerifyError(false);
      if (isRetry) {
        setDynamicUrl(null);
        if (onIframeStateChange) {
          onIframeStateChange(false);
        }
      }
      const res = await linkedinService.linkedinLogin(getLoginPayload());

      if (res?.success) {
        Toast({
          type: 'success',
          message: res.message || 'Cloud Browser opened successfully',
        });

        const cloudBrowserUrl = res?.data?.cloud_browser_url;
        const profileId = res?.data?.profile_id;

        setDynamicUrl(cloudBrowserUrl);
        setAccountData?.(prev => ({
          ...prev,
          cloud_browser_url: cloudBrowserUrl,
          profileId: profileId || prev?.profileId,
        }));

        if (isLinkedinLogin && onIframeStateChange) {
          onIframeStateChange(true);
        }
      } else {
        setLoginError(true);
        hasInitiatedLogin.current = false;
        Toast({
          type: 'error',
          message: res?.message || 'Something went wrong during LinkedIn login',
        });
      }
    } catch (error) {
      setLoginError(true);
      Toast({
        type: 'error',
        message: error?.message || 'Something went wrong during LinkedIn login',
      });
      hasInitiatedLogin.current = false;
    } finally {
      setLoginLoading(false);
    }
  }

  const handleRetryLogin = () => {
    hasInitiatedLogin.current = false;
    setLoginError(false);
    setVerifyError(false);
    handleLinkedinLogin(true);
  };

  const handleOpenCloudBrowser = () => {
    // Prevent duplicate calls
    if (loginLoading || dynamicUrl || hasInitiatedLogin.current) {
      return;
    }

    if (accountData?.cloud_browser_url) {
      setDynamicUrl(accountData.cloud_browser_url);
      if (onIframeStateChange) {
        onIframeStateChange(true);
      }
    } else if (isLinkedinLogin && activeProfile?.country) {
      handleLinkedinLogin();
    } else {
      Toast({ type: 'error', message: 'No Cloud Browser URL found!' });
    }
  };

  useEffect(() => {
    if (isLinkedinLogin && activeProfile?.country && !hasInitiatedLogin.current && !dynamicUrl && !loginLoading) {
      handleLinkedinLogin();
    }
  }, [isLinkedinLogin, activeProfile?.country]);


  async function handleVerify() {
    try {
      setLoading(true);
      const payload = {
        profileId: accountData?.profileId || activeProfile?.id,
      };

      const res = await linkedinService.confirmLinkedinLogin(payload);
      if (res.success) {
        Toast({
          type: 'success',
          message: res.message || 'LinkedIn account connected successfully',
        });

        setStep(4);
        if (onIframeStateChange) {
          onIframeStateChange(false);
        }
      } else {
        setVerifyError(true);
        Toast({
          type: 'error',
          message: res?.message || 'Verification failed',
        });
      }
    } catch (error) {
      setVerifyError(true);
      Toast({
        type: 'error',
        message: error?.message || 'Something went wrong',
      });
    } finally {
      setLoading(false);
    }
  }

  function handleCancel() {
    if (onIframeStateChange) {
      onIframeStateChange(false);
    }
    onClose();
    refetch();
  }

  useEffect(() => {
    if (accountData?.cloud_browser_url && !proxyLoading) {
      handleOpenCloudBrowser();
    }
  }, [accountData?.cloud_browser_url, proxyLoading]);


  return (
    <StyledConnectAccountSteps ContentCenter={!dynamicUrl} hasIframe={!!dynamicUrl}>
      {!dynamicUrl && !loginLoading && (
        <div className="connection-step">
          <FaLinkedin size={50} color="#0076B9" className="icon" />
          {proxyLoading && (
            <div className="leads-modal">
              <img src={stickLoading} alt="stickLoading" width={150} height={150} />
              <p>Your secure LinkedIn environment is being configured
                <br />After loading, log in to LinkedIn and press "Verify."</p>
              <p className="lock-text"> We never see or store your password.<CiLock size={20} color='var(--green-50)' /></p>
            </div>
          )}
        </div>
      )}

      {loginLoading && <Loaders viewLoader />}

      {dynamicUrl && <iframe src={dynamicUrl} title="Cloud Browser" />}
      <div className="btn-holder"></div>
      <div className="btn-holder">
        <div className="btn-wrap">
          <Button
            newStyle
            rounded
            type="button"
            variant="outline"
            width="155px"
            onClick={() => handleCancel()}>
            Cancel
          </Button>
        </div>
        <div className={loginError || verifyError ? 'btn-wrap-two error-btn' : 'btn-wrap-two'}>
          {(loginError || verifyError) && (
            <Button
              newStyle
              rounded
              variant="outline"
              type="button"
              width="155px"
              onClick={handleRetryLogin}
              loader={loginLoading}
              disabled={loginLoading || loading}>
              Retry
            </Button>
          )}
          <Button
            newStyle
            rounded
            variant="darkBlue"
            type="submit"
            width="155px"
            onClick={handleVerify}
            loader={loading}
            disabled={loading || loginLoading || !dynamicUrl}>
            Verify
          </Button>
        </div>
      </div>
    </StyledConnectAccountSteps>
  );
};

export default Connection;
